<template>
  <Accordion #default="{ isOpen, handleFocus }">
    <AccordionSection
      :path="'home.mp3'"
      :open="isOpen == 0"
      @open="handleFocus(0)"
    >
      <template #title>
        A Home for Black Students
      </template>
      <template #content>
        <p class="content">
          Hello and welcome to the Black Cultural Center located in Squires
          Student Center. Since its founding in 1991, it has served as the heart
          of the Black community at Virginia Tech. A vital component of Virginia
          Tech’s effort to create a more welcoming and inclusive environment,
          the BCC is utilized by the campus community for study space,
          organization meetings, exhibits, and receptions. Just as importantly,
          the BCC marked the first time where Black students truly had a home on
          the campus of Virginia Tech. We invite you to interact with the
          various points of interest around you to learn more about the center's
          history and its importance to the Black undergraduate experience.
        </p>
      </template>
    </AccordionSection>
    <AccordionSection
      :path="'brian.mp3'"
      :open="isOpen == 1"
      @open="handleFocus(1)"
    >
      <template #title>
        Brian Roberts’ BCC Vision
      </template>
      <template #content>
        <ImageComponent :image="roberts" />
        <p v-if="captions" class="content">
          In 1984, Brian Roberts drafted the first proposal for the
          establishment of the Black Cultural Center in Squires Student Center.
          A reflection of the increase in Black student enrollment, the proposal
          sought to offer a space for all students on campus to study,
          socialize, and build community centered around the Black student
          experience. In the years prior to this proposal, Roberts had already
          been a key figure among the Black undergraduate student population. He
          was instrumental in the founding of Virginia Tech’s chapter of the
          NAACP and the Black Organizations Council, which served as a liaison
          between Black student groups and the university's administration.
        </p>
        <p v-if="captions" class="content">
          Following graduation, Roberts worked as a community organizer and
          later went on to earn his Juris Doctorate from the University of
          Minnesota, during which he served as national chair for the National
          Black Law Students Association. After earning his law degree, Roberts
          worked as a trial attorney at community-based defender programs in
          Washington D.C. and New York.
        </p>
        <p v-if="captions" class="content">
          His commitment to Virginia Tech continued in his later years, serving
          as a member of the Black Cultural Center Alumni Advisory Board and
          helping with the endowment of three scholarships for Black students at
          Virginia Tech. Following his unfortunate passing in 2014, Roberts
          directed the entirety of his personal library be donated to the Black
          Cultural Center. Ranging from biographies to poems, the Brian K.
          Roberts library continues to be used by students today.
        </p>
      </template>
    </AccordionSection>
    <AccordionSection
      :path="'creation.mp3'"
      :open="isOpen == 2"
      @open="handleFocus(2)"
    >
      <template #title>
        Creation of the BCC
      </template>
      <template #content>
        <ImageComponent :image="ellison" />
        <p v-if="captions" class="content">
          After Brian Roberts drafted the proposal for the construction of the
          Black Cultural Center, Lisa Ellison, the President of the Black
          Organizations Council, was the one who made the formal proposal to the
          commission who would vote on it. Originally met with opposition,
          commission members debated the divisiveness of such a space on campus,
          with many believing that the BCC would entail the need for Cultural
          Centers for all groups on campus. Despite the concerns, the proposal
          passed by a vote of 7-5, with the majority of support coming from
          other student representatives rather than faculty and administration.
        </p>
        <p v-if="captions" class="content">
          Following its approval, initial plans for the Center placed it on the
          third floor, tucked away in the back of Squires. Thanks to continued
          lobbying by Black students and faculty, the plan was revised to have
          the BCC placed in a space of prominence, directly next to the entrance
          of the building. As is clear, the creation of the Black Cultural
          Center did not come without considerable resistance.
        </p>
        <p v-if="captions" class="content">
          With Virginia Tech being a predominately white institution, the BCC
          has given Black students a dedicated space on campus to feel accepted
          and welcome among a community of their peers, with many describing it
          as a place they can “let their hair down” after traversing a
          predominately white student body for the majority of their day.
        </p>
      </template>
    </AccordionSection>
    <AccordionSection :open="isOpen == 3" @open="handleFocus(3)">
      <template #title>
        Student Testimonies
      </template>
      <template #content>
        <ImageComponent :image="students" />
        <p class="content">
          Hear directly from students about the importance of the Black Cultural
          Center during their time as undergraduates.
        </p>
        <ImageComponent :image="williams" />
        <p v-if="captions" class="content">
          "It was only students and the Black Cultural Center. That was it.
          Sometimes I could go days without seeing myself, so that's why this
          place became so important to me. I think without having the center I
          don't think I would have graduated...to just be around people who are
          going through the same experiences, but we can share, we can cry
          together, we can let loose and use slang...it was really important,
          because once you step out of that space, you put your shield up
          again."
        </p>
        <ImageComponent :image="levi" />
        <p v-if="captions" class="content">
          “There was a perception that we always battled because people,
          especially the majority community, the white community would always
          say like, 'Well why does this place have to be separate?' It isn’t.
          You are welcome to come in, it’s just that when you come in the
          dialect will be different. Some of the social cues will be a bit
          different because this is where we feel most comfortable, and we’re
          going to talk the way that we’re accustomed to talking and we will not
          code switch like you don’t realize we do”
        </p>
      </template>
    </AccordionSection>
    <AccordionSection :open="isOpen == 4" @open="handleFocus(4)">
      <template #title>
        The Legacy Mural
      </template>
      <template #content>
        <ImageComponent :image="mural" />
        <p v-if="captions" class="content">
          In 1993, two years after the BCC opened, Matthew Winston Sr., class of
          1959, funded the legacy mural you see before you. After being alerted
          about the opportunity by his son, who also attended and worked at
          Virginia Tech, Winston couldn’t give up the opportunity to give back
          and leave his mark on the University that meant so much to him. Rather
          than focus on just the history of Black students at Virginia Tech, the
          Legacy Mural is about Black history from across the globe, with images
          ranging from ancient Egyptians to Rosa Parks.
        </p>
      </template>
    </AccordionSection>
  </Accordion>
</template>

<script>
import Accordion from "@/components/Accordion.vue";
import AccordionSection from "@/components/AccordionSection.vue";
import ImageComponent from "@/components/ImageComponent.vue";

export default {
  name: "BCCAccordion",
  components: {
    Accordion,
    AccordionSection,
    ImageComponent
  },
  props: {
    captions: {
      type: Boolean
    }
  },
  data() {
    return {
      roberts: {
        name: "Brian K. Roberts",
        path: "Brian K Roberts.png",
        caption: "Brian K. Roberts"
      },
      ellison: {
        name: "Lisa Ellison",
        path: "Lisa ellison.png",
        caption: "Lisa Ellison"
      },
      students: {
        name: "Student Testimonies",
        path: "Student Testimonies opening photo.jpg",
        caption: "Student Testimonies"
      },
      williams: {
        name: "Kimberly Williams",
        path: "kimberly williams.jpg",
        caption: "Kimberly Williams"
      },
      levi: {
        name: "Daniel Levi",
        path: "Daniel Levi.png",
        caption: "Daniel Levi"
      },
      mural: {
        name: "Mural",
        path: "Matthew Winston Sr. and Jr. in front of legacy mural.jpg",
        caption: "Matthew Winston Sr. and Jr. in front of the Legacy Mural"
      }
    };
  }
};
</script>
